// @givmo/board — the minute book: everything this reader's board has recorded.
//
// ══ THE ONE THING THIS PAGE MUST NOT DO ════════════════════════════════════════════════════════
//
// It must not tell a reader that something exists which they may not read. Executive-session scoping
// is applied in SQL, and `meta.total` is scoped with the page precisely so a `limit=1` offset walk
// cannot map the number and chronological position of the closed sessions somebody was not in — an
// empty page at an in-range offset IS the signal. So this page:
//
//   * renders what arrived and stops. There is no total compared against a page length, no "n more",
//     no gap indicator, and no row rendered as "restricted";
//   * searches, and filters by type and status, ON THE SERVER — the search box is the records list's
//     own title search (`q`, CBG-UX1), scoped with the same predicate as the page, so a closed session
//     a reader was not in is never found by searching. A search over one loaded page would silently
//     miss every record past it, in a book that grows without bound.
//
// ══ ONE LINE, A TABLE, AND THE DOWNLOAD (CBG-UX1, Jonathan's approved mockup m4-b) ═══════════════
//
// The page's one line is the ownership promise; the fine print sits behind "How this works". The
// records are a table — title, type, date, status — whose rows stack on a phone. Statuses are plain
// words; only "Awaiting approval" is coloured, because someone must act on it. A closed-session record
// says so in plain words with a lock. The two downloads are one "Download" menu, and the machine-
// readable copy's record count and fingerprint — read from the very bytes that were downloaded —
// open in a small dialog after it lands (the panel that used to show them is gone).
//
// ══ WHAT A COPY LEAVES OUT (CBG-RC12-EXPORT-WEB; frames e1–e4) ═══════════════════════════════════
//
// A copy holds the closed sessions' minutes sent to its reader. After either download, the dialog
// says which it left out, by their public facts only (the meeting, its day, the general purpose), in
// the readable copy's own approved words, read from what THAT copy said (`copy-closed-records.ts`):
// the machine-readable copy's body, the readable copy's header. A current director is offered
// "Include them too": the same kind of copy again with them in it, each recorded in its minutes'
// history as opened by them (opt-in, the orchestrator's C8). A readable copy that leaves nothing out
// still opens no dialog.
//
// ══ IMPORTING IS AN ACTION HERE, NOT A PLACE OF ITS OWN (D-CBG-9, 2026-09-28) ══════════════════
//
// Records from before the board used Givmo land in this book, reading "Imported". The import workspace
// is this page's action, offered only to a holder of `board_record:import` — a control a person cannot
// use is absent, never disabled with a note (Jonathan, 2026-09-28).
//
// ══ WHAT THE BOOK HOLDS, AND THE WAY TO MAKE A RECORD (CBG-UX8-BOOK; frames b1, b2) ═════════════
//
// Under the title, what the book holds; then, for a holder of the draft door (`board_record:write`:
// drafting minutes and starting a written consent are both it), the way to make a record, ending
// with "How this works". The page's old one line leads the fine print. Minutes a meeting is still
// drafting are listed "In progress" above the record, each opening its meeting's Minutes tab (the
// one place they are written, CBG-FIX8): the same list read, filtered ON THE SERVER to minutes in
// draft, so a draft past the first page is still there. The unfiltered table then doesn't repeat
// them; a filter or a search still finds them. An empty book adds the third way a record starts,
// importing, for a holder of `board_record:import`.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  isGivmoError,
  type OrgBoardOrgExportBoardMinuteBookResponse,
  type OrgBoardRecordOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  DataTable,
  Dialog,
  EmptyState,
  Icon,
  Menu,
  SearchInput,
  Select,
  Skeleton,
  saasButtonVariants,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { BookText, ChevronDown, Download, Lock } from 'lucide-react';
import { Link, useNavigate } from '../../board-routing';
import { BOARD_PAGES } from '../../board-routes';
import { Fingerprint } from '../../components/fingerprint';
import { PageShell } from '../../components/page-shell';
import { GroupLabel, HowThisWorks, StatusWord } from '../../components/fine-print';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import {
  closedRecordsOfManifest,
  type CopyClosedRecords,
  type LeftOutSession,
} from './copy-closed-records';
import { downloadMinuteBookDocument, downloadMinuteBookManifest } from './export-download';
import { exportWords as ew } from './export-words';
import {
  displayDate,
  extractRecords,
  meetingMinutesOf,
  recordKindLabelOf,
  recordStandingLabel,
  recordsListQuery,
} from './records-decode';
import { minuteBookWords as mb } from './minute-book-words';
// CBG-AI5-PROPOSE (frame m1): the one line while a batch of past records waits.
import { BookWaitingLine } from '../proposals/waiting-lines';
import { useWaitingBatches } from '../proposals/proposal-queries';

/**
 * How many records are asked for at a time.
 *
 * A minute book grows without bound, and one that silently stopped at a hundred would be showing a
 * board less than its own record — so a FULL page means "ask for more", and the control below says so.
 * That inference is a fact about the RESPONSE (it filled what was asked for), never a claim about
 * anything withheld: a short page simply offers nothing.
 */
const PAGE_SIZE = 100;

/** How long typing settles before the search goes to the server. */
const SEARCH_SETTLE_MS = 250;

/**
 * The longest search the server takes: the longest title a record can have (the backend's
 * `BOARD_TITLE_MAX_LENGTH`). A longer one is the server's 422, which would turn the whole book into
 * its load error, so the box stops there — no title could match past it anyway.
 */
const SEARCH_MAX_LENGTH = 300;

/** The dropdowns' "no filter" value — a Select needs a string. */
const ANY = 'any';

const TYPE_OPTIONS = [
  { value: ANY, label: 'All types' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'resolution', label: 'Resolutions' },
  { value: 'written_consent', label: 'Written consents' },
  { value: 'disclosure', label: 'Disclosures' },
  { value: 'acknowledgment', label: 'Acknowledgments' },
  { value: 'governing_document', label: 'Governing documents' },
  { value: 'meeting', label: 'Meeting records' },
];

const STATUS_OPTIONS = [
  { value: ANY, label: 'Any status' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Awaiting approval' },
  // The stored `approved` state holds three standings (CBG-UX3): an officer's approval, a filed
  // written consent and a recorded disclosure. The filter selects the stored state, so its option
  // says all three, in the rows' own words ("signed by everyone" since CBG-UX-COPY2's A6); each row
  // then reads its own word (`recordStandingLabel`).
  { value: 'approved', label: 'Approved, signed by everyone or recorded' },
  // CBG-UX-COPY2's A5b: the book's own word for a record from before Givmo, as each row reads it.
  { value: 'imported', label: 'From earlier records' },
  { value: 'discarded', label: 'Discarded' },
];

/**
 * How many minutes still in draft "In progress" asks for. Boards draft a meeting's minutes a few at a
 * time; a draft past this many keeps its row in the table, as every draft did before.
 */
const IN_PROGRESS_LIMIT = 100;

/** The fine print behind the page's one line. What the book is and keeps — never a promise about it. */
const HOW_THE_BOOK_WORKS = [
  // Approved (CBG-UX1): the page's old one line, moved here, first (CBG-UX8-BOOK, design.md §2).
  'These records are yours — download a complete copy anytime.',
  'A draft becomes part of the record when an officer approves it.',
  'An approved record doesn’t change. A correction is added beside it.',
  'Imported records are the ones your board kept before it used Givmo.',
  'Records are kept at least 10 years. Givmo never deletes a record on its own.',
] as const;

export function recordsQueryKey(
  orgRef: string | null,
  kind: string | null,
  state: string | null,
  search: string,
  limit: number,
): readonly unknown[] {
  return ['board-records', orgRef, 'browse', kind, state, search, limit];
}

/** The search as typed, settled for a moment before it is asked of the server. */
function useSettled(value: string, delayMs: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

export function RecordsPage() {
  const operations = useBoardOperations();
  const navigate = useNavigate();
  const { orgRef, isReady } = useBoardHost();
  // Importing older records is `board_record:import`: the action is offered only to its holder.
  const canImport = useBoardPermission('board_record:import');
  // CBG-UX8-BOOK: drafting minutes and starting a written consent are both the draft door,
  // `board_record:write`; the way to them is offered only to someone who can take it.
  const canWrite = useBoardPermission('board_record:write');
  // CBG-AI5-PROPOSE: shown to someone who can confirm a batch, while one waits.
  const waitingBatches = useWaitingBatches();
  const [kind, setKind] = useState<string>(ANY);
  const [state, setState] = useState<string>(ANY);
  const [searchInput, setSearchInput] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  // The copy just taken, its kind, and (once a director included them) what the copy before it left out.
  const [copy, setCopy] = useState<TakenCopy | null>(null);
  const search = useSettled(searchInput.trim(), SEARCH_SETTLE_MS);

  const kindFilter = kind === ANY ? null : kind;
  const stateFilter = state === ANY ? null : state;

  // A new search or filter starts the window over — a deeper window for one query means nothing for
  // another.
  useEffect(() => setLimit(PAGE_SIZE), [search]);

  const recordsQuery = useQuery({
    queryKey: recordsQueryKey(orgRef, kindFilter, stateFilter, search, limit),
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardRecordOut[]> => {
      // A typed const, so the search key the committed snapshot does not declare yet rides the wire.
      const query = recordsListQuery({ kind: kindFilter, state: stateFilter, search, limit });
      const { data } = await operations.officerListBoardRecords({
        path: { org_ref: orgRef ?? '' },
        query,
        throwOnError: true,
      });
      return extractRecords(data);
    },
  });

  // CBG-UX8-BOOK: the minutes meetings are still drafting — the same list read, filtered on the
  // server to the minutes kind in draft. Only a meeting's own minutes, or a closed session's, are
  // listed: their one door is the meeting's Minutes tab (CBG-FIX8). Minutes written in the book
  // itself keep their row in the table, where their own doors are.
  const inProgressQuery = useQuery({
    queryKey: ['board-records', orgRef, 'minutes-in-progress'],
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardRecordOut[]> => {
      const query = recordsListQuery({
        kind: 'minutes',
        state: 'draft',
        search: '',
        limit: IN_PROGRESS_LIMIT,
      });
      const { data } = await operations.officerListBoardRecords({
        path: { org_ref: orgRef ?? '' },
        query,
        throwOnError: true,
      });
      return extractRecords(data).filter((record) => meetingMinutesOf(record) !== null);
    },
  });
  const page = recordsQuery.data ?? [];
  // The two reads are cached apart, so the page can be the fresher of them: minutes it shows in
  // another state (approved since "In progress" was read) leave the list.
  const pageStates = new Map(page.map((record) => [record.id, record.state]));
  const inProgress = (inProgressQuery.data ?? []).filter((record) => {
    const fresher = pageStates.get(record.id);
    return fresher === undefined || fresher === 'draft';
  });

  const narrowed = kindFilter !== null || stateFilter !== null || search.length > 0;
  // The unfiltered book lists each of those once, under "In progress", and not again in the table;
  // a filter or a search finds them as it finds anything. A row is hidden only while its OWN copy is
  // still a draft, so a record the page says is no longer one never vanishes from the table.
  const listedAbove = new Set(narrowed ? [] : inProgress.map((record) => record.id));
  const records = page.filter(
    (record) => !(listedAbove.has(record.id) && record.state === 'draft'),
  );
  // A fact about the RESPONSE — it filled what was asked for — so it counts what arrived.
  const mayBeMore = page.length >= limit;
  // The table waits for both reads, or a draft would show in it a moment before it moves up.
  const settled = recordsQuery.isSuccess && !inProgressQuery.isPending;

  const failed = (caught: unknown) =>
    toast.error(
      isGivmoError(caught) ? caught.message : 'We couldn’t put that together. Please try again.',
    );
  const takeDocument = useMutation({
    mutationFn: () => downloadMinuteBookDocument(orgRef ?? '', operations),
    // The readable copy opens the dialog only when it left something out, as ITS response says.
    onSuccess: (closed) =>
      closed !== null && closed.leftOut.length > 0
        ? setCopy({ kind: 'document', manifest: null, closed, before: [] })
        : undefined,
    onError: failed,
  });
  const takeManifest = useMutation({
    mutationFn: () => downloadMinuteBookManifest(orgRef ?? '', operations),
    // The figures come from the DOWNLOADED manifest, not a second read — so what the dialog says
    // describes the file that is now on the officer's machine.
    onSuccess: (downloaded) =>
      setCopy({
        kind: 'manifest',
        manifest: downloaded,
        closed: closedRecordsOfManifest(downloaded),
        before: [],
      }),
    onError: failed,
  });
  // "Include them too": the same kind of copy again, with the closed sessions this director wasn't
  // sent, each recorded as opened. The dialog then describes the new copy.
  const includeThem = useMutation({
    mutationFn: async (taken: TakenCopy): Promise<TakenCopy | null> => {
      const options = { includeClosedSessions: true };
      const before = taken.closed.leftOut;
      if (taken.kind === 'manifest') {
        const downloaded = await downloadMinuteBookManifest(orgRef ?? '', operations, options);
        return {
          kind: 'manifest',
          manifest: downloaded,
          closed: closedRecordsOfManifest(downloaded),
          before,
        };
      }
      const closed = await downloadMinuteBookDocument(orgRef ?? '', operations, options);
      // A readable copy that says nothing has nothing for the dialog to describe.
      return closed === null || (closed.leftOut.length === 0 && closed.opened.length === 0)
        ? null
        : { kind: 'document', manifest: null, closed, before };
    },
    onSuccess: (next) => setCopy(next),
    onError: failed,
  });

  const open = (record: OrgBoardRecordOut) =>
    void navigate({ to: '/records/$recordId', params: { recordId: record.id } });

  return (
    <PageShell
      title="Minute book"
      description={
        <>
          {mb.holds}
          {/* The way to make a record is the header's second line, ending with "How this works", so
              the header reads as two lines (frame b1). Without the draft door, the fine print
              follows the first line. */}
          {canWrite ? (
            <span className="mt-8 block" data-testid="records-way">
              <WayToARecord /> <HowThisWorks testId="records-how" lines={HOW_THE_BOOK_WORKS} />
            </span>
          ) : (
            <>
              {' '}
              <HowThisWorks testId="records-how" lines={HOW_THE_BOOK_WORKS} />
            </>
          )}
        </>
      }
      actions={
        <div className="flex flex-wrap items-center gap-8">
          {canImport ? (
            // A real link styled as the page's action: the workspace is a destination, so
            // open-in-a-new-tab and copy-address keep working.
            <Link
              to={BOARD_PAGES.importBatches}
              className={saasButtonVariants({
                size: 'md',
                tone: 'secondary',
                className: 'no-underline',
              })}
              data-testid="records-import"
            >
              Import older records
            </Link>
          ) : null}
          <Menu.Root>
            <Menu.Trigger
              render={
                <Button
                  appearance="saas"
                  tone="secondary"
                  leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                  trailingIcon={<Icon icon={ChevronDown} size={15} aria-hidden />}
                  loading={takeDocument.isPending || takeManifest.isPending}
                  disabled={orgRef === null}
                  testId="records-download"
                />
              }
            >
              Download
            </Menu.Trigger>
            <Menu.Content saas align="end" className="min-w-[264px]" testId="records-download-menu">
              <Menu.Item
                saas
                onClick={() => takeDocument.mutate()}
                testId="records-export-document"
              >
                <span className="flex flex-col gap-2">
                  <span>Readable copy</span>
                  <span className="text-s12 font-w400 text-text-tertiary">
                    A plain-text document of the book
                  </span>
                </span>
              </Menu.Item>
              <Menu.Item
                saas
                onClick={() => takeManifest.mutate()}
                testId="records-export-manifest"
              >
                <span className="flex flex-col gap-2">
                  <span>Machine-readable copy</span>
                  <span className="text-s12 font-w400 text-text-tertiary">
                    A JSON file other software can read
                  </span>
                </span>
              </Menu.Item>
            </Menu.Content>
          </Menu.Root>
        </div>
      }
      testId="page-records"
    >
      {/* CBG-AI5-PROPOSE (frame m1): above the records, while a batch waits. */}
      <BookWaitingLine batches={waitingBatches} />
      {/* CBG-UX8-BOOK (frame b1): minutes still being drafted, above the record. */}
      {inProgress.length > 0 ? <InProgress records={inProgress} /> : null}
      <div
        className="flex flex-col gap-10 sm:flex-row sm:items-center"
        data-testid="records-toolbar"
      >
        <SearchInput
          placeholder="Search records"
          aria-label="Search the minute book by title"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onClear={() => setSearchInput('')}
          maxLength={SEARCH_MAX_LENGTH}
          containerClassName="w-full sm:w-[280px]"
          testId="records-search"
        />
        <div className="grid grid-cols-2 gap-10 sm:flex">
          <Select
            appearance="saas"
            aria-label="Type"
            options={TYPE_OPTIONS}
            value={kind}
            onValueChange={(next) => {
              setLimit(PAGE_SIZE);
              setKind(next ?? ANY);
            }}
            className="sm:w-[184px]"
            testId="records-filter-kind"
          />
          <Select
            appearance="saas"
            aria-label="Status"
            options={STATUS_OPTIONS}
            value={state}
            onValueChange={(next) => {
              setLimit(PAGE_SIZE);
              setState(next ?? ANY);
            }}
            className="sm:w-[184px]"
            testId="records-filter-state"
          />
        </div>
      </div>

      {(recordsQuery.isPending || (recordsQuery.isSuccess && !settled)) && isReady ? (
        <Skeleton className="h-[240px] w-full rounded-xl" testId="records-loading" />
      ) : null}

      {recordsQuery.isError ? (
        <Card testId="records-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this board’s minute book.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void recordsQuery.refetch()}
              testId="records-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {settled && page.length === 0 && !narrowed ? (
        <EmptyState
          testId="records-empty"
          icon={<Icon icon={BookText} size={20} aria-hidden />}
          title="Nothing in the minute book yet"
          // CBG-UX8-BOOK (frame b2): the header says what the book holds and the way to make a
          // record, so the empty state adds only the third way a record starts, for someone who
          // can take it. A reader who can do none of the three sees the title alone.
          description={
            canImport ? (
              <>
                {mb.olderRecords}{' '}
                <Link
                  to={BOARD_PAGES.importBatches}
                  className={WAY_LINK}
                  data-testid="records-empty-import"
                >
                  {mb.importThem}
                </Link>
              </>
            ) : undefined
          }
        />
      ) : null}

      {settled && records.length === 0 && narrowed ? (
        <p
          className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
          data-testid="records-none-in-filter"
        >
          Nothing matches.
        </p>
      ) : null}

      {settled && records.length > 0 ? <RecordsTable records={records} onOpen={open} /> : null}

      {/* CBG-UX8-BOOK: an unfiltered book whose every record is still in progress says so under
          its list. */}
      {settled && !narrowed && page.length > 0 && records.length === 0 ? (
        <p
          className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
          data-testid="records-nothing-approved"
        >
          {mb.nothingApprovedYet}
        </p>
      ) : null}

      {/* Offered because the page came back FULL — a fact about the response, never a claim about
          anything withheld. A short page offers nothing, and says nothing about why it is short. */}
      {mayBeMore ? (
        <Button
          appearance="saas"
          tone="secondary"
          className="self-start"
          loading={recordsQuery.isFetching}
          onClick={() => setLimit((current) => current + PAGE_SIZE)}
          testId="records-more"
        >
          Show more of the book
        </Button>
      ) : null}

      {copy !== null ? (
        <ExportResultDialog
          copy={copy}
          including={includeThem.isPending}
          onInclude={() => includeThem.mutate(copy)}
          onClose={() => setCopy(null)}
        />
      ) : null}
    </PageShell>
  );
}

/** A way-finding link inside a line of text. */
const WAY_LINK =
  'font-w600 text-brand-700 outline-none transition-colors hover:text-brand-800 hover:underline focus-visible:underline';

/** "Writing minutes? Open the meeting in Meetings. Deciding without a meeting? Start a written
 *  consent." — both links, for someone who can take them (CBG-UX8-BOOK, frame b1). */
function WayToARecord() {
  return (
    <>
      {mb.writingMinutes}{' '}
      <Link to={BOARD_PAGES.meetings} className={WAY_LINK} data-testid="records-way-meetings">
        {mb.openTheMeeting}
      </Link>
      . {mb.decidingWithout}{' '}
      <Link to={BOARD_PAGES.consents} className={WAY_LINK} data-testid="records-way-consents">
        {mb.startAConsent}
      </Link>
      .
    </>
  );
}

/**
 * Minutes still being drafted, each opening its meeting's Minutes tab — a closed session's own at
 * that session (CBG-UX8-BOOK, frame b1). Each says its state as the book does: "Draft", or "Out for
 * signatures" for a closed session's minutes the directors who were there are asked to sign.
 */
function InProgress({ records }: { records: readonly OrgBoardRecordOut[] }) {
  return (
    <section className="flex flex-col gap-10" data-testid="records-in-progress">
      <GroupLabel>{mb.inProgress}</GroupLabel>
      <Card className="overflow-hidden">
        <ul className="divide-y divide-border-subtle">
          {records.map((record) => {
            const minutesOf = meetingMinutesOf(record);
            if (minutesOf === null) return null;
            return (
              <li key={record.id} data-testid={`records-in-progress-${record.id}`}>
                {/* One grid, two readings, as a meeting's row: on a phone the way to the minutes
                    follows the lines; from `sm` it sits at the row's right. */}
                <Link
                  to="/meetings/$meetingId/minutes"
                  params={{ meetingId: minutesOf.meetingId }}
                  {...(minutesOf.segmentId !== null
                    ? { search: { session: minutesOf.segmentId } }
                    : {})}
                  className="group grid grid-cols-1 items-baseline gap-x-16 gap-y-4 px-20 py-14 outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover sm:grid-cols-[minmax(0,1fr)_auto]"
                  data-testid={`records-in-progress-link-${record.id}`}
                >
                  <span className="min-w-0 font-sans text-s15 font-w600 tracking-givmo text-text-strong transition-colors group-hover:text-brand-700">
                    {record.title}
                  </span>
                  <span className="font-sans text-s13 font-w600 tracking-givmo text-brand-700 max-sm:order-last sm:text-right">
                    {mb.openTheMinutes}
                  </span>
                  <span className="flex min-w-0 flex-col gap-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                    {record.confidentiality === 'executive_session' ? (
                      <span
                        className="inline-flex items-center gap-4 text-s12"
                        data-testid={`records-in-progress-confidentiality-${record.id}`}
                      >
                        <Icon icon={Lock} size={12} aria-hidden />
                        Closed session
                      </span>
                    ) : null}
                    <span data-testid={`records-in-progress-state-${record.id}`}>
                      {mb.stateChanged(
                        recordStandingLabel(record),
                        displayDate(record.updated_at ?? record.created_at ?? null),
                      )}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </Card>
    </section>
  );
}

function RecordStatus({ record }: { record: OrgBoardRecordOut }) {
  return (
    <StatusWord
      tone={record.state === 'submitted' ? 'act' : 'plain'}
      testId={`records-status-${record.id}`}
    >
      {recordStandingLabel(record)}
    </StatusWord>
  );
}

function RecordsTable({
  records,
  onOpen,
}: {
  records: OrgBoardRecordOut[];
  onOpen: (record: OrgBoardRecordOut) => void;
}) {
  const columns: DataTableColumn<OrgBoardRecordOut>[] = [
    {
      id: 'title',
      header: 'Title',
      cell: (record) => (
        <span className="flex min-w-0 flex-col gap-2">
          {/* A real link, so the book is operable from the keyboard and a record opens in a new
              tab; the row's own click is the mouse's shortcut to the same place. */}
          <Link
            to="/records/$recordId"
            params={{ recordId: record.id }}
            className="font-w600 text-text-strong outline-none hover:text-brand-700 focus-visible:underline"
            data-testid={`records-link-${record.id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {record.title}
          </Link>
          {record.confidentiality === 'executive_session' ? (
            <span
              className="inline-flex items-center gap-4 text-s12 text-text-tertiary"
              data-testid={`records-confidentiality-${record.id}`}
            >
              <Icon icon={Lock} size={12} aria-hidden />
              Closed session
            </span>
          ) : null}
        </span>
      ),
      stacked: 'heading',
    },
    {
      id: 'type',
      header: 'Type',
      cell: (record) => <span className="text-text-secondary">{recordKindLabelOf(record)}</span>,
      width: '176px',
    },
    {
      id: 'date',
      header: 'Date',
      cell: (record) => (
        <span className="text-text-secondary tabular-nums">
          {record.occurred_on ? displayDate(record.occurred_on) : '—'}
        </span>
      ),
      width: '136px',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (record) => <RecordStatus record={record} />,
      width: '160px',
    },
  ];
  return (
    <Card className="overflow-hidden" testId="records-table">
      <DataTable
        columns={columns}
        rows={records}
        rowKey={(record) => record.id}
        rowTestId={(record) => `records-card-${record.id}`}
        onRowClick={onOpen}
        stackAt="sm"
      />
    </Card>
  );
}

/**
 * What the machine-readable copy just downloaded contains: its record count and its fingerprint,
 * read from the very manifest that was saved. A dialog rather than a toast because the fingerprint is
 * worth reading and copying, and a toast is gone before anyone could.
 *
 * It says ONE short line, and that line is NEW wording: it rewords the retired panel's "Taking the
 * same copy again reproduces that fingerprint, so a difference means the records changed" as "gives
 * the same fingerprint, so a different one means the records changed". The panel's two other
 * reviewed sentences sit behind "How this works" VERBATIM (the orchestrator's by-eye pass: say less):
 * each record is covered by identity, kind, state and its own content fingerprint, and the copy is
 * the one THIS reader received. Plan §3.4 bans the verif- root, so the line says the same copy
 * "gives the same fingerprint", and claims nothing beyond that.
 */
const HOW_THE_FINGERPRINT_WORKS = [
  'It covers each record by its identity, kind, state and its own content fingerprint — not the references a record points at, which are held still by the record store itself.',
  'It describes the copy you received: a record you are not entitled to read is not in it, and is not counted.',
  // CBG-YR3-EXPORT (approved, `export-words.ts`): the copy's roster, which no fingerprint covers.
  ew.rosterNotCovered,
] as const;

/** The copy just taken: its kind, its manifest (the machine-readable copy's), what it said about its
 *  closed records, and what the copy before it left out (once a director included them). */
interface TakenCopy {
  kind: 'manifest' | 'document';
  manifest: OrgBoardOrgExportBoardMinuteBookResponse | null;
  closed: CopyClosedRecords;
  before: LeftOutSession[];
}

/** One left-out session: the meeting and its day, then its general purpose beneath, quieter. */
function LeftOutItem({ session }: { session: LeftOutSession }) {
  return (
    <li className="flex flex-col gap-2">
      <span className="font-sans text-s14 font-w500 leading-h15 tracking-givmo text-text-strong">
        {ew.meetingAndDay(session.meeting_title, displayDate(session.held_on ?? null))}
      </span>
      <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {session.purpose_label?.trim() || ew.noPurpose}
      </span>
    </li>
  );
}

function SessionList({ sessions }: { sessions: readonly LeftOutSession[] }) {
  return (
    <ul className="flex flex-col gap-10">
      {sessions.map((session, index) => (
        <LeftOutItem key={`${session.meeting_id}-${index}`} session={session} />
      ))}
    </ul>
  );
}

function ExportResultDialog({
  copy,
  including,
  onInclude,
  onClose,
}: {
  copy: TakenCopy;
  including: boolean;
  onInclude: () => void;
  onClose: () => void;
}) {
  const { manifest, closed } = copy;
  const count = manifest?.record_count ?? 0;
  const title = copy.kind === 'manifest' ? 'Your machine-readable copy' : ew.readableTitle;
  // Below the machine-readable copy's own lines, each section sits under a rule (e1–e3); the readable
  // copy's dialog has no lines above it (e4).
  const section =
    copy.kind === 'manifest'
      ? 'flex flex-col gap-8 border-t border-border-subtle pt-16'
      : 'flex flex-col gap-8';

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !including && onClose()}>
      <Dialog.Content
        aria-label={title}
        className="flex max-h-[85vh] flex-col gap-16 overflow-y-auto"
        testId="records-export-result"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>
        {manifest !== null ? (
          <>
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              <span className="font-w600 text-text-strong" data-testid="records-export-count">
                {count} {count === 1 ? 'record' : 'records'}
              </span>{' '}
              in the copy you just took. Its fingerprint is
            </p>
            {/* The WHOLE fingerprint, on its own and selectable: a shortened one cannot be compared
                against a later copy, and noting it down is why this dialog exists. */}
            <Fingerprint value={manifest.manifest_hash ?? ''} testId="records-export-hash" />
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              Taking the same copy again gives the same fingerprint, so a different one means the
              records changed.{' '}
              <HowThisWorks testId="records-export-how" lines={HOW_THE_FINGERPRINT_WORKS} />
            </p>
          </>
        ) : null}

        {closed.leftOut.length > 0 ? (
          <section className={section} data-testid="records-export-left-out">
            <GroupLabel>{ew.notInThisExport}</GroupLabel>
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {ew.keptForThePeople}
            </p>
            <SessionList sessions={closed.leftOut} />
            {closed.exporterIsACurrentDirector ? (
              // The step and what it records, read together (as d2's opening step reads).
              <div className="mt-4 flex flex-col items-start gap-10">
                <p
                  className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                  data-testid="records-export-include-line"
                >
                  {ew.youCanInclude} {ew.openingIsRecorded}
                </p>
                <Button
                  appearance="saas"
                  tone="secondary"
                  loading={including}
                  onClick={onInclude}
                  testId="records-export-include"
                >
                  {ew.includeThemToo}
                </Button>
              </div>
            ) : null}
          </section>
        ) : null}

        {closed.opened.length > 0 && copy.before.length > 0 ? (
          <section className={section} data-testid="records-export-opened">
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {ew.openedByYou(copy.before.length)}
            </p>
            <SessionList sessions={copy.before} />
          </section>
        ) : null}

        <div className="flex justify-end">
          <Button
            appearance="saas"
            tone="primary"
            disabled={including}
            onClick={onClose}
            testId="records-export-done"
          >
            Done
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
